import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, CheckCircle2, ShieldAlert } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { WEB_AUTH_UI_ENABLED } from "@/config/publicConfig";
import { useAuthStore } from "@/store/useAuthStore";
import { formatCode, formatForInput, normalizeCode } from "./redemptionCode";
import { redeemCode, RedeemError, type RedeemSuccess } from "./redeemService";

/** 登录往返期间暂存兑换码（sessionStorage：关掉标签页就消失，不落盘）。 */
const PENDING_CODE_KEY = "pending_redeem_code";

function readPendingCode(): string {
  try {
    return sessionStorage.getItem(PENDING_CODE_KEY) ?? "";
  } catch {
    return "";
  }
}
function writePendingCode(code: string) {
  try {
    if (code) sessionStorage.setItem(PENDING_CODE_KEY, code);
    else sessionStorage.removeItem(PENDING_CODE_KEY);
  } catch {
    /* 隐私模式下 sessionStorage 可能不可用，忽略 */
  }
}

/**
 * 众筹特典码兑换页（官网 /redeem）。
 *
 * iOS 用户只能在这里兑换（App Store 3.1.1 禁止 App 内用 license key 解锁功能），
 * 所以本页不做任何 App 端引导，App 只会读取已经生效的 VIP。
 * 邮件里的链接形如 /redeem#code=XXXX：`#` 之后的内容不会发给服务器、不进访问日志，
 * 读取后立刻从地址栏抹掉。
 */
export default function RedeemPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, profile, initialized, fetchProfile } = useAuthStore();

  const [input, setInput] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [result, setResult] = useState<RedeemSuccess | null>(null);

  // 读取 #code=... 或登录往返前暂存的码，并立即把地址栏里的码抹掉
  useEffect(() => {
    const m = location.hash.match(/[#&]code=([^&]+)/);
    if (m) {
      let decoded = m[1];
      try {
        decoded = decodeURIComponent(m[1]);
      } catch {
        /* 含非法 % 编码时按原文处理，不能让页面崩溃 */
      }
      const fromUrl = formatForInput(decoded);
      setInput(fromUrl);
      writePendingCode(fromUrl);
      window.history.replaceState(null, "", location.pathname + location.search);
    } else {
      const pending = readPendingCode();
      if (pending) setInput(pending);
    }
    // 只在首次进入时读取
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 进入页面时重新拉一次资料：用户可能刚在 App 里通过年龄确认，store 里还是旧状态
  const [profileState, setProfileState] = useState<"loading" | "ready" | "failed">("loading");
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setProfileState("loading");
    fetchProfile()
      .then(() => !cancelled && setProfileState("ready"))
      .catch(() => !cancelled && setProfileState("failed"));
    return () => {
      cancelled = true;
    };
  }, [userId, fetchProfile]);

  // 与服务端 redeem_supporter_code 的判定一致：metadata 标记或游客专用邮箱后缀
  const isGuest =
    user?.user_metadata?.is_guest === true || (user?.email ?? "").toLowerCase().endsWith("@mamamatch.guest");
  const ageApproved = profile?.age_verified_status === "approved";
  const accountLabel = user?.email ?? profile?.display_name ?? user?.id ?? "";
  const profileLoading = !!user && profileState === "loading";
  const profileFailed = !!user && (profileState === "failed" || (profileState === "ready" && !profile));

  const handleReview = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCode(null);
    const canonical = normalizeCode(input);
    if (!canonical) {
      // 校验位不对：本地直接拦下，不请求服务器
      setErrorCode("INVALID_FORMAT");
      return;
    }
    setConfirming(canonical);
  };

  const handleConfirm = async () => {
    if (!confirming) return;
    setSubmitting(true);
    setErrorCode(null);
    try {
      const r = await redeemCode(confirming);
      setResult(r);
      setInput("");
      writePendingCode("");
    } catch (err) {
      setErrorCode(err instanceof RedeemError ? err.code : "DEFAULT");
    } finally {
      setSubmitting(false);
      setConfirming(null);
    }
  };

  const errorText = (code: string) => {
    const key = `redeem.errors.${code}`;
    const text = t(key);
    return text === key ? t("redeem.errors.DEFAULT") : text;
  };

  const reset = () => {
    setResult(null);
    setErrorCode(null);
    setInput("");
  };

  const renderBody = () => {
    if (!initialized || profileLoading) {
      return (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-pink-500" />
        </div>
      );
    }

    if (!user) {
      return (
        <div className="space-y-4">
          <p className="text-gray-700">{t("redeem.login_required")}</p>
          {WEB_AUTH_UI_ENABLED ? (
            <Link
              to="/login?redirect=/redeem"
              className="block text-center w-full py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-medium"
            >
              {t("redeem.login_btn")}
            </Link>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {t("redeem.login_unavailable")}
            </p>
          )}
          {input && (
            <p className="text-xs text-gray-500">
              {t("redeem.code_label")}: <span className="font-mono">{input}</span>
            </p>
          )}
        </div>
      );
    }

    if (isGuest) {
      return <Blocked text={t("redeem.guest_blocked")} />;
    }
    if (profileFailed) {
      return <Blocked text={t("redeem.errors.DEFAULT")} />;
    }
    if (!ageApproved) {
      return <Blocked text={t("redeem.age_required")} />;
    }

    if (result) {
      return (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
          <h2 className="text-xl font-semibold text-gray-900">{t("redeem.success_title")}</h2>
          {result.status === "already_redeemed" ? (
            <p className="text-gray-700">{t("redeem.success_already")}</p>
          ) : (
            <p className="text-gray-700">{t("redeem.success_added", { days: result.vip_days_added })}</p>
          )}
          <p className="text-gray-900 font-medium">
            {t("redeem.success_remaining", { days: result.wallet_days_after })}
          </p>
          {result.badge && (
            <p className="text-pink-600">
              {t("redeem.success_badge", { badge: t(`redeem.badges.${result.badge}`) })}
            </p>
          )}
          <p className="text-sm text-gray-500">{t("redeem.success_note")}</p>
          <div className="flex flex-col gap-2 pt-2">
            <button onClick={reset} className="w-full py-2 border border-pink-500 text-pink-600 rounded-lg hover:bg-pink-50">
              {t("redeem.register_another")}
            </button>
            <Link to="/" className="text-sm text-gray-500 hover:text-pink-600">
              {t("redeem.back_home")}
            </Link>
          </div>
        </div>
      );
    }

    return (
      <form onSubmit={handleReview} className="space-y-4">
        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
          <span className="text-gray-500">{t("redeem.account_label")}：</span>
          <span className="font-medium text-gray-900 break-all">{accountLabel}</span>
        </div>

        <div>
          <label htmlFor="redeem-code" className="block text-sm font-medium text-gray-700 mb-1">
            {t("redeem.code_label")}
          </label>
          <input
            id="redeem-code"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={input}
            onChange={(e) => {
              setInput(formatForInput(e.target.value));
              setErrorCode(null);
            }}
            placeholder={t("redeem.code_placeholder")}
            className="w-full p-3 border rounded-lg font-mono text-lg tracking-wider text-center focus:ring-pink-500 focus:border-pink-500"
            required
          />
        </div>

        {errorCode && (
          <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">{errorText(errorCode)}</div>
        )}

        <button
          type="submit"
          disabled={submitting || input.replace(/-/g, "").length === 0}
          className="w-full py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 transition-colors font-medium"
        >
          {t("redeem.submit")}
        </button>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-pink-50 flex flex-col">
      <SeoHead
        path="redeem"
        title="特典コードの登録 - BamaBang"
        description="Redeem your BamaBang crowdfunding supporter code."
      />
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/favicon.png" alt="Logo" className="w-9 h-9" />
            <span className="font-bold text-gray-900">BamaBang</span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 max-w-xl w-full mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <Gift className="w-7 h-7 text-pink-500" />
            <h1 className="text-2xl font-bold text-gray-900">{t("redeem.title")}</h1>
          </div>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">{t("redeem.subtitle")}</p>
          {renderBody()}
        </div>

        <div className="mt-6 text-xs text-gray-500 leading-relaxed">
          <p className="font-medium text-gray-600 mb-1">{t("redeem.notes_title")}</p>
          <ul className="list-disc pl-5 space-y-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <li key={n}>{t(`redeem.note_${n}`)}</li>
            ))}
          </ul>
        </div>
      </main>

      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t("redeem.confirm_title")}</h2>
            <p className="text-sm text-gray-700">
              {t("redeem.confirm_body", { code: formatCode(confirming) })}
            </p>
            <p className="text-sm font-medium text-gray-900 break-all bg-gray-50 rounded-lg p-3">{accountLabel}</p>
            <p className="text-sm text-amber-700">{t("redeem.confirm_warning")}</p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirming(null)}
                disabled={submitting}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t("redeem.cancel")}
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="flex-1 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50"
              >
                {submitting ? t("redeem.submitting") : t("redeem.confirm_ok")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Blocked({ text }: { text: string }) {
  return (
    <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
      <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
      <p>{text}</p>
    </div>
  );
}
